import type { JSONSchema } from '@carboncopyco/editor-utils';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { EditJsonButton } from './EditJsonButton';

export interface TableCellProps<T> {
  value: T;
  onChange: (value: T) => void;
  jsonSchema: JSONSchema;
  readOnly?: boolean;
}

export const TableCell = <T,>({
  value: initialValue,
  jsonSchema,
  onChange,
  readOnly,
}: TableCellProps<T>) => {
  const [value, setValue] = useState<T>(initialValue);

  const jsonType = jsonSchema?.type;
  const isNumeric = jsonType === 'number' || jsonType === 'integer';
  const isBoolean = jsonType === 'boolean';
  const isJson = jsonType === 'object' || jsonType === 'array';

  const onBlur = (value) => {
    try {
      if (isNumeric) {
        value = convertToNumber(value);
      } else if (isBoolean) {
        value = convertToBoolean(value);
      }
    } catch (_error) {
      console.log("Can't convert the value to its schema type");
      value = initialValue;
    }

    setValue(value);
    if (value !== initialValue) {
      onChange(value);
    }
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  if (isJson) {
    return (
      <EditJsonButton
        readOnly={readOnly}
        json={value as Record<string, unknown> | unknown[]}
        onChange={(value) => {
          setValue(value as T);
          onBlur(value);
        }}
      />
    );
  }

  return (
    <input
      className={classNames(
        'focus:outline-ds-primary-300',
        isNumeric && 'text-right',
        readOnly && 'pointer-events-none'
      )}
      value={value as string}
      onChange={(e) => setValue(e.target.value as T)}
      onBlur={() => onBlur(value)}
      readOnly={readOnly}
    />
  );
};

const convertToNumber = (value: unknown) => {
  if (Number.isNaN(Number(value))) {
    throw new Error('Value is not a number');
  }
  return Number(value);
};

const convertToBoolean = (value: unknown) => {
  if (value !== 'true' && value !== 'false') {
    throw new Error('Value is not a boolean');
  }
  return JSON.parse(value);
};
